<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useArdhisasaTicketsStore } from 'dashboard/stores/ardhisasaTickets';

const emit = defineEmits(['close', 'created']);

const { t } = useI18n();
const store = useArdhisasaTicketsStore();

const error = ref('');
const success = ref('');

const form = ref({
  customer_name: '',
  customer_type_id: '',
  phone_number: '',
  country_code: '+254',
  email: '',
  identification_type_id: '',
  identification_number: '',
  entity_id: '',
  department_id: '',
  section_id: '',
  source_id: '',
  source_reference_number: '',
  subject: '',
  information: '',
  ticket_type: 'SPECIFIC',
  ticket_category: 'REGULAR',
  parcels_raw: '',
});

const isSpecific = computed(() => form.value.ticket_type === 'SPECIFIC');
const isAnonymous = computed(() => form.value.ticket_category === 'ANONYMOUS');
const hasDraft = ref(false);

// Restore draft on open
onMounted(() => {
  if (store.ticketDraft) {
    form.value = { ...store.ticketDraft };
    hasDraft.value = true;
  }
});

// Sync form changes to store draft
watch(form, val => store.setTicketDraft(val), { deep: true });

// Helper: extract display label from a lookup item (APIs return varied shapes)
function labelOf(item) {
  return item?.name || item?.label || item?.value || item || '';
}

function idOf(item) {
  return item?.id ?? item?.value ?? item ?? '';
}

// Watch entity → load departments
watch(
  () => form.value.entity_id,
  async val => {
    form.value.department_id = '';
    form.value.section_id = '';
    if (val) await store.fetchDepartments(val);
  }
);

// Watch department → load sections
watch(
  () => form.value.department_id,
  async val => {
    form.value.section_id = '';
    if (val) await store.fetchSections(val);
  }
);

async function submit() {
  error.value = '';
  success.value = '';

  const parcels = isSpecific.value
    ? form.value.parcels_raw
        .split(',')
        .map(s => s.trim())
        .filter(Boolean)
    : [];

  const payload = {
    customer_name: form.value.customer_name,
    customer_type: isSpecific.value ? form.value.customer_type_id : undefined,
    phone_number: form.value.phone_number,
    country_code: isAnonymous.value ? form.value.country_code : undefined,
    email: isAnonymous.value ? form.value.email : undefined,
    identification_type: form.value.identification_type_id,
    identification_number: form.value.identification_number,
    entity: form.value.entity_id,
    department: form.value.department_id,
    section: form.value.section_id || '',
    source: form.value.source_id,
    source_reference_number: form.value.source_reference_number,
    subject: form.value.subject,
    information: form.value.information,
    ticket_type: form.value.ticket_type,
    ticket_category: form.value.ticket_category,
    parcels_of_interest: isSpecific.value ? parcels : undefined,
    slug: undefined,
  };

  // Remove undefined keys
  Object.keys(payload).forEach(k => {
    if (payload[k] === undefined) delete payload[k];
  });

  try {
    const data = await store.createTicket(payload, isAnonymous.value);
    success.value = t('ARDHISASA.TICKETS.CREATE.SUCCESS', {
      reference: data.request_id || data.reference_number || '',
    });
    store.clearTicketDraft();
    setTimeout(() => emit('created'), 1500);
  } catch (e) {
    const msg =
      e?.response?.data?.details || t('ARDHISASA.TICKETS.CREATE.ERROR');
    error.value = typeof msg === 'string' ? msg : JSON.stringify(msg);
  }
}
</script>

<template>
  <div
    class="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto py-8 bg-black/40"
  >
    <div
      class="bg-white dark:bg-n-solid-2 rounded-xl shadow-xl w-full max-w-2xl mx-4"
    >
      <!-- Header -->
      <div
        class="flex items-center justify-between px-6 py-4 border-b border-n-weak"
      >
        <div class="flex items-center gap-2">
          <h2 class="text-sm font-semibold text-n-slate-12">
            {{ t('ARDHISASA.TICKETS.CREATE.TITLE') }}
          </h2>
          <span
            v-if="hasDraft"
            class="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700"
          >
            {{ t('ARDHISASA.TICKETS.CREATE.DRAFT_RESTORED') }}
          </span>
        </div>
        <button
          class="p-1 rounded hover:bg-n-alpha-2 text-n-slate-11"
          @click="emit('close')"
        >
          <span class="i-lucide-x size-4" />
        </button>
      </div>

      <form class="px-6 py-5 space-y-6" @submit.prevent="submit">
        <!-- Ticket type + category -->
        <div class="grid grid-cols-2 gap-4">
          <div>
            <label class="block text-xs font-medium text-n-slate-11 mb-1">
              {{ t('ARDHISASA.TICKETS.CREATE.TICKET_TYPE') }}
            </label>
            <select
              v-model="form.ticket_type"
              class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-alpha-2 text-n-slate-12"
            >
              <option value="SPECIFIC">
                {{ t('ARDHISASA.TICKETS.CREATE.TYPE_SPECIFIC') }}
              </option>
              <option value="GENERAL">
                {{ t('ARDHISASA.TICKETS.CREATE.TYPE_GENERAL') }}
              </option>
            </select>
          </div>
          <div>
            <label class="block text-xs font-medium text-n-slate-11 mb-1">
              {{ t('ARDHISASA.TICKETS.CREATE.TICKET_CATEGORY') }}
            </label>
            <select
              v-model="form.ticket_category"
              class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-alpha-2 text-n-slate-12"
            >
              <option value="REGULAR">
                {{ t('ARDHISASA.TICKETS.CREATE.CATEGORY_REGULAR') }}
              </option>
            </select>
          </div>
        </div>

        <!-- Customer section -->
        <div>
          <p
            class="text-xs font-semibold text-n-slate-11 uppercase tracking-wide mb-3"
          >
            {{ t('ARDHISASA.TICKETS.CREATE.CUSTOMER_SECTION') }}
          </p>
          <div class="grid grid-cols-2 gap-4">
            <div class="col-span-2">
              <label class="block text-xs font-medium text-n-slate-11 mb-1">
                {{ t('ARDHISASA.TICKETS.CREATE.CUSTOMER_NAME') }}
              </label>
              <input
                v-model="form.customer_name"
                required
                type="text"
                class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-alpha-2 text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand"
              />
            </div>

            <div v-if="isSpecific">
              <label class="block text-xs font-medium text-n-slate-11 mb-1">
                {{ t('ARDHISASA.TICKETS.CREATE.CUSTOMER_TYPE') }}
              </label>
              <select
                v-model="form.customer_type_id"
                class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-alpha-2 text-n-slate-12"
              >
                <option value="">
                  {{ t('ARDHISASA.TICKETS.CREATE.SELECT_PLACEHOLDER') }}
                </option>
                <option
                  v-for="ct in store.lookups.customerTypes"
                  :key="idOf(ct)"
                  :value="idOf(ct)"
                >
                  {{ labelOf(ct) }}
                </option>
              </select>
            </div>

            <div>
              <label class="block text-xs font-medium text-n-slate-11 mb-1">
                {{ t('ARDHISASA.TICKETS.CREATE.PHONE') }}
              </label>
              <input
                v-model="form.phone_number"
                required
                type="text"
                class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-alpha-2 text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand"
              />
            </div>

            <template v-if="isAnonymous">
              <div>
                <label class="block text-xs font-medium text-n-slate-11 mb-1">
                  {{ t('ARDHISASA.TICKETS.CREATE.COUNTRY_CODE') }}
                </label>
                <input
                  v-model="form.country_code"
                  type="text"
                  class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-alpha-2 text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand"
                />
              </div>
              <div>
                <label class="block text-xs font-medium text-n-slate-11 mb-1">
                  {{ t('ARDHISASA.TICKETS.CREATE.EMAIL') }}
                </label>
                <input
                  v-model="form.email"
                  type="email"
                  class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-alpha-2 text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand"
                />
              </div>
            </template>

            <div>
              <label class="block text-xs font-medium text-n-slate-11 mb-1">
                {{ t('ARDHISASA.TICKETS.CREATE.ID_TYPE') }}
              </label>
              <select
                v-model="form.identification_type_id"
                class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-alpha-2 text-n-slate-12"
              >
                <option value="">
                  {{ t('ARDHISASA.TICKETS.CREATE.SELECT_PLACEHOLDER') }}
                </option>
                <option
                  v-for="it in store.lookups.identificationTypes"
                  :key="idOf(it)"
                  :value="it.code"
                >
                  {{ labelOf(it) }}
                </option>
              </select>
            </div>

            <div>
              <label class="block text-xs font-medium text-n-slate-11 mb-1">
                {{ t('ARDHISASA.TICKETS.CREATE.ID_NUMBER') }}
              </label>
              <input
                v-model="form.identification_number"
                required
                type="text"
                class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-alpha-2 text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand"
              />
            </div>
          </div>
        </div>

        <!-- Ticket details section -->
        <div>
          <p
            class="text-xs font-semibold text-n-slate-11 uppercase tracking-wide mb-3"
          >
            {{ t('ARDHISASA.TICKETS.CREATE.TICKET_SECTION') }}
          </p>
          <div class="grid grid-cols-2 gap-4">
            <div>
              <label class="block text-xs font-medium text-n-slate-11 mb-1">
                {{ t('ARDHISASA.TICKETS.CREATE.ENTITY') }}
              </label>
              <select
                v-model="form.entity_id"
                class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-alpha-2 text-n-slate-12"
              >
                <option value="">
                  {{ t('ARDHISASA.TICKETS.CREATE.SELECT_PLACEHOLDER') }}
                </option>
                <option
                  v-for="ent in store.lookups.entities"
                  :key="idOf(ent)"
                  :value="idOf(ent)"
                >
                  {{ labelOf(ent) }}
                </option>
              </select>
            </div>

            <div>
              <label class="block text-xs font-medium text-n-slate-11 mb-1">
                {{ t('ARDHISASA.TICKETS.CREATE.DEPARTMENT') }}
              </label>
              <select
                v-model="form.department_id"
                :disabled="!form.entity_id"
                class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-alpha-2 text-n-slate-12 disabled:opacity-50"
              >
                <option value="">
                  {{ t('ARDHISASA.TICKETS.CREATE.SELECT_PLACEHOLDER') }}
                </option>
                <option
                  v-for="dept in store.lookups.departments"
                  :key="idOf(dept)"
                  :value="idOf(dept)"
                >
                  {{ labelOf(dept) }}
                </option>
              </select>
            </div>

            <div>
              <label class="block text-xs font-medium text-n-slate-11 mb-1">
                {{ t('ARDHISASA.TICKETS.CREATE.SECTION') }}
              </label>
              <select
                v-model="form.section_id"
                :disabled="!form.department_id"
                class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-alpha-2 text-n-slate-12 disabled:opacity-50"
              >
                <option value="">
                  {{ t('ARDHISASA.TICKETS.CREATE.SELECT_PLACEHOLDER') }}
                </option>
                <option
                  v-for="sec in store.lookups.sections"
                  :key="idOf(sec)"
                  :value="idOf(sec)"
                >
                  {{ labelOf(sec) }}
                </option>
              </select>
            </div>

            <div>
              <label class="block text-xs font-medium text-n-slate-11 mb-1">
                {{ t('ARDHISASA.TICKETS.CREATE.SOURCE') }}
              </label>
              <select
                v-model="form.source_id"
                class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-alpha-2 text-n-slate-12"
              >
                <option value="">
                  {{ t('ARDHISASA.TICKETS.CREATE.SELECT_PLACEHOLDER') }}
                </option>
                <option
                  v-for="src in store.lookups.channelSources"
                  :key="idOf(src)"
                  :value="idOf(src)"
                >
                  {{ labelOf(src) }}
                </option>
              </select>
            </div>

            <div>
              <label class="block text-xs font-medium text-n-slate-11 mb-1">
                {{ t('ARDHISASA.TICKETS.CREATE.SOURCE_REF') }}
              </label>
              <input
                v-model="form.source_reference_number"
                required
                type="text"
                class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-alpha-2 text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand"
              />
            </div>

            <div class="col-span-2">
              <label class="block text-xs font-medium text-n-slate-11 mb-1">
                {{ t('ARDHISASA.TICKETS.CREATE.SUBJECT') }}
              </label>
              <input
                v-model="form.subject"
                required
                type="text"
                class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-alpha-2 text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand"
              />
            </div>

            <div class="col-span-2">
              <label class="block text-xs font-medium text-n-slate-11 mb-1">
                {{ t('ARDHISASA.TICKETS.CREATE.INFORMATION') }}
              </label>
              <textarea
                v-model="form.information"
                required
                rows="3"
                class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-alpha-2 text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand resize-none"
              />
            </div>

            <div v-if="isSpecific" class="col-span-2">
              <label class="block text-xs font-medium text-n-slate-11 mb-1">
                {{ t('ARDHISASA.TICKETS.CREATE.PARCELS') }}
              </label>
              <input
                v-model="form.parcels_raw"
                type="text"
                :placeholder="t('ARDHISASA.TICKETS.CREATE.PARCELS_HINT')"
                class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-alpha-2 text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand"
              />
            </div>
          </div>
        </div>

        <!-- Feedback -->
        <p
          v-if="error"
          class="text-xs text-n-ruby-9 bg-red-50 px-3 py-2 rounded-lg"
        >
          {{ error }}
        </p>
        <p
          v-if="success"
          class="text-xs text-green-700 bg-green-50 px-3 py-2 rounded-lg"
        >
          {{ success }}
        </p>

        <!-- Actions -->
        <div class="flex justify-end gap-3 pt-2 border-t border-n-weak">
          <button
            type="button"
            class="px-4 py-2 text-sm rounded-lg border border-n-weak text-n-slate-11 hover:bg-n-alpha-2"
            @click="emit('close')"
          >
            {{ t('ARDHISASA.TICKETS.CREATE.CANCEL') }}
          </button>
          <button
            type="submit"
            :disabled="store.uiFlags.creatingTicket"
            class="px-4 py-2 text-sm rounded-lg bg-[#71481d] text-white font-medium hover:bg-[#5a3915] disabled:opacity-50"
          >
            {{
              store.uiFlags.creatingTicket
                ? t('ARDHISASA.TICKETS.LOADING')
                : t('ARDHISASA.TICKETS.CREATE.SUBMIT')
            }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>
